/**
 * Centralized Tool Registry for VoiceChat tools
 * 
 * This registry manages all voice chat tools in a single place, providing:
 * - Simple tool registration with direct execution functions
 * - Central argument parsing and validation
 * - Consistent error handling
 * - Clear configuration APIs
 */
import { useKnowledgeGraphStore, EntityType, type Entity, type Relation } from '~/stores/knowledgeGraphStore';

// export an enum of tool names and their string values
export enum ToolName {
  ADD_CALENDAR_EVENTS = 'addCalendarEvents',
  GET_CALENDAR_EVENTS = 'getCalendarEvents',
  DELETE_CALENDAR_EVENTS = 'deleteCalendarEvents',
  GET_CURRENT_TIME = 'getCurrentTime',
  QUERY_KNOWLEDGE_GRAPH = 'queryKnowledgeGraph',
  ADD_KNOWLEDGE_GRAPH_ENTITY = 'addKnowledgeGraphEntity',
  ADD_KNOWLEDGE_GRAPH_RELATION = 'addKnowledgeGraphRelation',
  GET_KNOWLEDGE_GRAPH_ENTITIES = 'getKnowledgeGraphEntities',
  STOP_CHAT = 'stopChat',
}

// Common interface for a message in the conversation
export interface Message {
  id: string;
  role: string;
  text: string;
  timestamp: string;
  status: string;
  isFinal?: boolean;
}

// Helper to safely parse JSON arguments
export function safeJsonParse<T>(jsonString: string, defaultValue: T): T {
  try {
    return JSON.parse(jsonString) as T;
  } catch (error) {
    return defaultValue;
  }
}

// Standard context passed to all tools
export interface ToolContext {
  callId: string;
  dataChannel: RTCDataChannel;
  completedCalls: Set<string>;
  logger: any;
  conversation: Message[];
}

// Function call payload from OpenAI
export interface FunctionCallPayload {
  call_id: string;
  name: string;
  arguments: string;
}

// Function completion payload from OpenAI
export interface FunctionCompletionPayload {
  call_id: string;
  name?: string;
}

// Standardized tool response interface
export interface ToolResponse<T = any> {
  success: boolean;
  result?: T;
  error?: string;
}

// Message types for WebRTC data channel
export enum MessageType {
  FUNCTION_CALL_OUTPUT = 'function_call_output',
  CONTINUATION = 'response.create'
}

// OpenAI tool schema definition
export interface OpenAIToolSchema {
  type: string;
  name: string;
  description: string;
  parameters: {
    type: string;
    properties: Record<string, any>;
    required: string[];
  };
}

// Tool definition interface for registration
export interface ToolDefinition<TArgs = any, TResult = any> {
  name: string;
  execute: (args: TArgs, context: ToolContext) => Promise<TResult>;
  formatResult?: (result: TResult, args?: TArgs) => Message | null;
  getContinuationInstructions: () => string;
  validateArgs?: (args: any) => TArgs;
  // Properties used for dependency management
  dependencies?: string[];
  config?: Record<string, any>;
  // Add OpenAI schema for this tool
  openAISchema?: OpenAIToolSchema;
}

// Interface for overriding tool properties
export interface ToolPropertyOverride {
  // Tool description override
  description?: string;
  // Tool continuation instructions override
  continuationInstructions?: string;
  // Parameters properties overrides
  parameterDescriptions?: Record<string, string>;
  // Examples for parameters
  parameterExamples?: Record<string, string[]>;
}

// Map of tool overrides by tool name
export interface ToolOverridesMap {
  [toolName: string]: ToolPropertyOverride;
}

// Tool configuration object
export interface ToolConfig {
  [key: string]: any;
}

/**
 * ToolRegistry - Central manager for all voice chat tools
 */
class ToolRegistry {
  private tools: Map<string, ToolDefinition> = new Map();
  private toolConfigs: Map<string, ToolConfig> = new Map();
  private toolOverrides: Map<string, ToolPropertyOverride> = new Map();
  private initialized: boolean = false;
  
  /**
   * Register a tool with the registry
   */
  register<TArgs, TResult>(tool: ToolDefinition<TArgs, TResult>): void {
    // Apply any overrides before registering
    const overriddenTool = this.applyOverridesToTool(tool);
    this.tools.set(overriddenTool.name, overriddenTool);
    
    // Initialize tool config if it doesn't exist
    if (!this.toolConfigs.has(overriddenTool.name)) {
      this.toolConfigs.set(overriddenTool.name, {});
    }
  }
  
  /**
   * Apply overrides to a tool before registration
   */
  private applyOverridesToTool<TArgs, TResult>(tool: ToolDefinition<TArgs, TResult>): ToolDefinition<TArgs, TResult> {
    const override = this.toolOverrides.get(tool.name);
    if (!override) {
      return tool;
    }
    
    // Create a deep copy of the tool to avoid mutating the original
    const overriddenTool = { ...tool };
    
    // Apply continuation instructions override if present
    if (override.continuationInstructions) {
      const originalGetContinuationInstructions = overriddenTool.getContinuationInstructions;
      overriddenTool.getContinuationInstructions = () => override.continuationInstructions;
    }
    
    // Apply schema overrides if present
    if (overriddenTool.openAISchema && (override.description || override.parameterDescriptions || override.parameterExamples)) {
      overriddenTool.openAISchema = { ...overriddenTool.openAISchema };
      
      // Override description
      if (override.description) {
        overriddenTool.openAISchema.description = override.description;
      }
      
      // Override parameter descriptions and examples
      if (override.parameterDescriptions || override.parameterExamples) {
        overriddenTool.openAISchema.parameters = { 
          ...overriddenTool.openAISchema.parameters,
          properties: { ...overriddenTool.openAISchema.parameters.properties }
        };
        
        // Apply parameter description overrides
        if (override.parameterDescriptions) {
          for (const [paramName, paramDescription] of Object.entries(override.parameterDescriptions)) {
            if (overriddenTool.openAISchema.parameters.properties[paramName]) {
              overriddenTool.openAISchema.parameters.properties[paramName] = {
                ...overriddenTool.openAISchema.parameters.properties[paramName],
                description: paramDescription
              };
            }
          }
        }
        
        // Apply parameter examples overrides
        if (override.parameterExamples) {
          for (const [paramName, examples] of Object.entries(override.parameterExamples)) {
            if (overriddenTool.openAISchema.parameters.properties[paramName]) {
              overriddenTool.openAISchema.parameters.properties[paramName] = {
                ...overriddenTool.openAISchema.parameters.properties[paramName],
                examples: examples
              };
            }
          }
        }
      }
    }
    
    return overriddenTool;
  }
  
  /**
   * Set tool property overrides
   * These will be applied when tools are registered
   */
  setToolOverrides(overrides: ToolOverridesMap): void {
    // Clear existing overrides
    this.toolOverrides.clear();
    
    // Add new overrides
    for (const [toolName, override] of Object.entries(overrides)) {
      this.toolOverrides.set(toolName, override);
      
      // If tool is already registered, re-register it to apply overrides
      if (this.tools.has(toolName)) {
        const tool = this.tools.get(toolName);
        // Re-register the tool with overrides
        if (tool) {
          this.register(tool);
        }
      }
    }
  }
  
  /**
   * Get tool property overrides
   */
  getToolOverrides(): ToolOverridesMap {
    const overridesMap: ToolOverridesMap = {};
    this.toolOverrides.forEach((override, toolName) => {
      overridesMap[toolName] = override;
    });
    return overridesMap;
  }
  
  /**
   * Configure a tool with dependencies
   */
  configureTool(toolName: string, config: ToolConfig): void {
    if (!this.tools.has(toolName)) {
      throw new Error(`Cannot configure tool "${toolName}": Tool not registered`);
    }
    
    this.toolConfigs.set(toolName, {
      ...this.toolConfigs.get(toolName),
      ...config
    });
  }
  
  /**
   * Get configuration for a tool
   */
  getToolConfig(toolName: string): ToolConfig {
    return this.toolConfigs.get(toolName) || {};
  }
  
  /**
   * Check if a tool is registered
   */
  hasToolRegistered(name: string): boolean {
    return this.tools.has(name);
  }
  
  /**
   * Get all registered tool names
   */
  getRegisteredToolNames(): string[] {
    return Array.from(this.tools.keys());
  }
  
  /**
   * Clear all registered tools
   */
  clearTools(): void {
    this.tools.clear();
    this.toolConfigs.clear();
    this.initialized = false;
  }
  
  /**
   * Clear all tool overrides
   */
  clearToolOverrides(): void {
    this.toolOverrides.clear();
  }
  
  /**
   * Execute a tool with the given payload
   */
  async executeToolCall(payload: FunctionCallPayload, context: ToolContext): Promise<void> {
    const { call_id, name, arguments: argsString } = payload;
    const logger = context.logger;
    
    // Check if call was already completed
    if (context.completedCalls.has(call_id)) {
      logger.info(`Skipping duplicate ${name} call with ID: ${call_id}`);
      return;
    }
    
    logger.debug(`ToolRegistry: Executing tool ${name} with arguments:`, {
      callId: call_id,
      argsLength: argsString?.length || 0,
      argsPreview: argsString?.substring(0, 100) + (argsString?.length > 100 ? '...' : '') || '{}'
    });
    
    // Check if tool exists
    const tool = this.tools.get(name);
    if (!tool) {
      logger.error(`No tool registered for function: ${name}`);
      
      // Send error response with standardized format
      const errorResponse: ToolResponse = {
        success: false,
        error: `Tool "${name}" not found`
      };
      
      this.sendToolResponse(context, {
        callId: call_id, 
        type: MessageType.FUNCTION_CALL_OUTPUT,
        response: errorResponse
      });
      
      return;
    }
    
    try {
      // Parse arguments
      const rawArgs = safeJsonParse(argsString, {});
      
      // Log the parsed arguments for debugging
      logger.debug(`ToolRegistry: Parsed arguments for ${name}:`, rawArgs);
      
      // Validate arguments if needed
      const args = tool.validateArgs ? tool.validateArgs(rawArgs) : rawArgs;
      
      // Execute the tool
      const result = await tool.execute(args, context);

      logger.debug(`ToolRegistry: Tool ${name} executed with result:`, result);
      
      // Mark call as completed
      context.completedCalls.add(call_id);
      
      // Add result message to conversation if formatter is provided
      if (tool.formatResult && result) {
        const message = tool.formatResult(result, args);
        if (message) {
          context.conversation.push(message);
        }
      }
      
      // Send success response
      const successResponse: ToolResponse = {
        success: true,
        result: result
      };
      
      // Send function output response
      this.sendToolResponse(context, {
        callId: call_id,
        type: MessageType.FUNCTION_CALL_OUTPUT,
        response: successResponse
      });
      
      // Send continuation instructions immediately after
      logger.debug(`ToolRegistry: Sending continuation instructions for ${name}`, {continuationInstructions: tool.getContinuationInstructions()});
      this.sendToolResponse(context, {
        callId: call_id,
        type: MessageType.CONTINUATION,
        instructions: tool.getContinuationInstructions()
      });
      
    } catch (error) {
      // Improved error logging with stack trace and more details
      logger.error(`Error executing tool ${name}:`, error);
      logger.error(`Error details:`, { 
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : 'No stack trace available',
        toolName: name,
        callId: call_id
      });
      
      // Create standardized error response
      const errorResponse: ToolResponse = {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
      
      // Send error response with standardized format
      this.sendToolResponse(context, {
        callId: call_id,
        type: MessageType.FUNCTION_CALL_OUTPUT,
        response: errorResponse
      });
      
      // Add error message to conversation
      context.conversation.push({
        id: Date.now().toString(),
        role: 'assistant',
        text: `I couldn't complete the ${name} operation: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date().toISOString(),
        status: 'complete',
        isFinal: true
      });
    }
  }
  
  /**
   * Send a tool response to the client
   * Unified method to handle all types of responses
   */
  private sendToolResponse(
    context: ToolContext, 
    options: {
      callId: string;
      type: MessageType;
      response?: ToolResponse;
      instructions?: string;
    }
  ): void {
    const { callId, type, response, instructions } = options;
    
    if (!context.dataChannel || context.dataChannel.readyState !== 'open') {
      context.logger.warn(`Cannot send response - data channel not available or not open`);
      return;
    }
    
    try {
      let message: any;
      
      if (type === MessageType.FUNCTION_CALL_OUTPUT) {
        // Function call output response
        message = {
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: callId,
            output: JSON.stringify(response)
          }
        };
      } else if (type === MessageType.CONTINUATION) {
        // Continuation instructions response
        message = {
          type: "response.create",
          response: {
            instructions
          }
        };
      } else {
        context.logger.error(`Unknown message type: ${type}`);
        return;
      }
      
      // Send the message
      context.dataChannel.send(JSON.stringify(message));
      context.logger.debug(`Sent ${type} message for call ID: ${callId}`);
    } catch (error) {
      context.logger.error(`Error sending ${type} message:`, error);
    }
  }
  
  /**
   * Get tool APIs for configuration
   */
  getToolAPIs(): Record<string, any> {
    // Build APIs dynamically based on registered tools
    const apis: Record<string, any> = {};
    
    this.tools.forEach((tool, name) => {
      apis[name] = this.getToolAPI(name);
    });
    
    return apis;
  }
  
  /**
   * Get API for a specific tool
   */
  getToolAPI(toolName: string): any {
    // This method would be implemented by each tool to return its specific API
    // Example: For calendar tools, this would return setCalendarItemsProvider, etc.
    return {};
  }
  
  /**
   * Mark registry as initialized
   */
  setInitialized(): void {
    this.initialized = true;
  }
  
  /**
   * Check if the registry is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
  
  /**
   * Get all OpenAI tool schemas
   */
  getAllToolSchemas(): OpenAIToolSchema[] {
    const schemas: OpenAIToolSchema[] = [];
    
    this.tools.forEach(tool => {
      if (tool.openAISchema) {
        schemas.push(tool.openAISchema);
      }
    });
    
    return schemas;
  }
}

// Create and export singleton instance
export const toolRegistry = new ToolRegistry();

/**
 * Helper function to create standard message
 */
export function createMessage(text: string, role: string = 'assistant'): Message {
  return {
    id: Date.now().toString(),
    role,
    text,
    timestamp: new Date().toISOString(),
    status: 'complete',
    isFinal: true
  };
} 